import React, { useState, useMemo } from 'react';
import type { FC } from 'react';
import {
  Body,
  Box,
  Collapsible,
  Divider,
  Glyph,
  Link,
} from '@edreamsodigeo/prisma-design-system';
import { useTranslation, createFeature } from '@edreamsodigeo/frontend-core';

import type {
  CollapsibleFareConditionsProps,
  ComponentDecoratorProps,
} from './config/types';
import { useFlowTools } from '@/flowTools/useFlowTools';
import { TRAINS_FARE_CONDITIONS } from '@/versionedComponents';
import type { CommercialBrand } from '@/generated/graphql';

function getLastNonEmptyIndex(arr: any[][]): number {
  for (let i = arr.length - 1; i >= 0; i--) {
    if (arr[i].length > 0) return i;
  }
  return -1;
}

const MobileDecorator: FC<{ open: boolean; onToggle: () => void }> = ({
  children,
  open,
  onToggle,
}) => {
  const { t } = useTranslation();
  return (
    <Box
      paddingY="base"
      color="neutral.0"
      data-testid="collapsible-fare-conditions-mobile"
    >
      <Box display="flex" alignItems="center" style={{ cursor: 'pointer' }}>
        <Link variant="inline" onClick={onToggle}>
          <Body type="small" color="neutral.80">
            {open
              ? t('fareConditions.flightSummary.titleHide')
              : t('fareConditions.flightSummary.titleShow')}
          </Body>
        </Link>
      </Box>
      <Collapsible open={open}>{children}</Collapsible>
    </Box>
  );
};

const DesktopDecorator: FC<{ open: boolean; onToggle: () => void }> = ({
  children,
  open,
  onToggle,
}) => {
  const { t } = useTranslation();
  return (
    <Box
      padding="base"
      backgroundColor="neutral.20"
      data-testid="collapsible-fare-conditions-desktop"
    >
      <Box
        display="flex"
        alignItems="center"
        onClick={onToggle}
        style={{ cursor: 'pointer' }}
      >
        <Body type="xSmall" color="neutral.80">
          {open
            ? t('fareConditions.flightSummary.titleHide')
            : t('fareConditions.flightSummary.titleShow')}
        </Body>
        <Box ml="small">
          {open ? (
            <Glyph
              category="Arrows"
              name="ChevronUp"
              size="tiny"
              color="neutral.80"
            />
          ) : (
            <Glyph
              category="Arrows"
              name="ChevronDown"
              size="tiny"
              color="neutral.80"
            />
          )}
        </Box>
      </Box>
      <Collapsible open={open}>{children}</Collapsible>
    </Box>
  );
};

const SegmentFareConditions: FC<{
  segmentTitle: string;
  brands: string[];
  t: (key: string) => string;
}> = ({ segmentTitle, brands, t }) => (
  <Box>
    <Box mb="2x_small">
      <Body type="xSmall" color="neutral.80">
        {segmentTitle}
        {brands[0] && <>: {t('fareConditions.title.' + brands[0])}</>}
      </Body>
    </Box>
    {brands.map((brand) => {
      const description = t('fareConditions.description.' + brand);

      return (
        <Body type="xSmall" color="neutral.70" key={brand}>
          {description && <Box>{description}</Box>}
        </Body>
      );
    })}
  </Box>
);

const SegmentDivider: FC = () => (
  <Box my="small">
    <Divider orientation="horizontal" type="dashed" />
  </Box>
);

const hasThisBrandTextTranslations = (
  brand: CommercialBrand,
  t: (key: string) => string
): brand is { id: string; name: string } =>
  !!brand &&
  !!brand.id &&
  (Boolean(t('fareConditions.title.' + brand.id)) ||
    Boolean(t('fareConditions.description.' + brand.id)));

const ComponentDecorator: FC<ComponentDecoratorProps> = ({
  isMobile,
  open,
  onToggle,
  children,
}) =>
  isMobile ? (
    <MobileDecorator open={open} onToggle={onToggle}>
      {children}
    </MobileDecorator>
  ) : (
    <DesktopDecorator open={open} onToggle={onToggle}>
      {children}
    </DesktopDecorator>
  );

const CollapsibleFareConditions: FC<CollapsibleFareConditionsProps> = ({
  segments,
}) => {
  const { t } = useTranslation();
  const { isMobile } = useFlowTools();
  const [open, setOpen] = useState(false);

  const commercialBrandsBySegment = useMemo(
    () =>
      segments.map((segment) => {
        const brands = segment.sections
          .map((section) => section.commercialBrand)
          .filter(
            (brand): brand is CommercialBrand =>
              !!brand && hasThisBrandTextTranslations(brand, t)
          )
          .map((brand) => brand.id);

        return Array.from(new Set(brands));
      }),
    [segments, t]
  );

  const anyHasTranslation = commercialBrandsBySegment.some(
    (brands) => brands.length > 0
  );

  if (!anyHasTranslation) {
    return null;
  }

  const lastSegmentWithTranslations = getLastNonEmptyIndex(
    commercialBrandsBySegment
  );

  return (
    <ComponentDecorator
      isMobile={isMobile}
      open={open}
      onToggle={() => setOpen((v) => !v)}
    >
      <Box mt="small">
        {commercialBrandsBySegment.map((brands, segmentIdx) => {
          if (brands.length === 0) return null;
          const segmentTitle =
            segmentIdx === 0
              ? t('summary.segment.departure')
              : t('summary.segment.return');
          const isLast = segmentIdx === lastSegmentWithTranslations;
          return (
            <Box key={segments[segmentIdx].id}>
              <SegmentFareConditions
                segmentTitle={segmentTitle}
                brands={brands}
                t={t}
              />
              {!isLast && <SegmentDivider />}
            </Box>
          );
        })}
      </Box>
    </ComponentDecorator>
  );
};

export const CollapsibleFareConditionsVersioned = createFeature({
  name: TRAINS_FARE_CONDITIONS,
  variations: [null, CollapsibleFareConditions],
  version: 1,
});

export default CollapsibleFareConditions;
